import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';

import { defaultDateRange } from '@/lib/date-range';
import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { DailyCostChart } from '@/components/admin/llm/daily-cost-chart';
import { LlmDateRangePicker } from '@/components/admin/llm/llm-date-range-picker';
import { OrgHeadlineStats } from '@/components/admin/llm/org-headline-stats';
import { PerFeatureTable } from '@/components/admin/llm/per-feature-table';
import { PerModelTable } from '@/components/admin/llm/per-model-table';
import { TopUsersTable } from '@/components/admin/llm/top-users-table';
import { WindowLabel } from '@/components/admin/llm/window-label';
import { Button } from '@/components/ui/button';
import { useAdminLlmUsageDetail } from '@/hooks/data/admin/use-llm';
import { ROUTES } from '@/constants/routes';
import { SECTION_TITLE } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

const searchSchema = z.object({
  start: z.string().date().optional(),
  end: z.string().date().optional(),
});

export const Route = createFileRoute('/_authenticated/admin/llm-usage/$orgId')({
  validateSearch: searchSchema,
  staticData: { title: 'LLM usage — org detail' },
  component: AdminLlmUsageDetailPage,
});

function AdminLlmUsageDetailPage(): React.ReactElement {
  const { orgId } = Route.useParams();
  const { start, end } = Route.useSearch();
  const navigate = Route.useNavigate();

  const orgIdNum = Number(orgId);

  const defaultRange = useMemo(() => defaultDateRange(30), []);
  const rangeStart = start ?? defaultRange.start;
  const rangeEnd = end ?? defaultRange.end;

  const detailQuery = useAdminLlmUsageDetail(orgIdNum, {
    start: rangeStart,
    end: rangeEnd,
  });
  const detail = detailQuery.data;

  const handleApply = (range: { start?: string; end?: string }): void => {
    void navigate({
      search: (s) => ({ ...s, start: range.start, end: range.end }),
    });
  };

  return (
    <main>
      <AdminPageHeader
        title={
          detail
            ? `LLM usage — ${detail.organization_name} (#${orgId})`
            : `LLM usage — Org #${orgId}`
        }
        description="Per-organization LLM cost, reliability, and usage breakdown"
      />

      <div className="flex flex-col gap-8 px-4 py-6 sm:px-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to={ROUTES.ADMIN_LLM_USAGE} search={(s) => s}>
              ← Back to summary
            </Link>
          </Button>
        </div>

        {detail && (
          <WindowLabel start={detail.window_start} end={detail.window_end} />
        )}

        <LlmDateRangePicker
          key={`${rangeStart}-${rangeEnd}`}
          start={rangeStart}
          end={rangeEnd}
          onApply={handleApply}
        />

        {detailQuery.isLoading && (
          <p className="text-fg-3 text-fs-13">Loading…</p>
        )}

        {detailQuery.isError && (
          <p className="text-danger-700 text-fs-13">
            Failed to load usage data for this organisation.
          </p>
        )}

        {detail && (
          <>
            <OrgHeadlineStats stats={detail.headline} margin={detail.margin} />

            <section>
              <h2 className={cn(SECTION_TITLE, 'mb-3')}>Daily cost</h2>
              <DailyCostChart data={detail.daily} />
            </section>

            <section>
              <h2 className={cn(SECTION_TITLE, 'mb-3')}>Per feature</h2>
              <PerFeatureTable data={detail.per_feature} />
            </section>

            <section>
              <h2 className={cn(SECTION_TITLE, 'mb-3')}>Per model</h2>
              <PerModelTable data={detail.per_model} />
            </section>

            <section>
              <h2 className={cn(SECTION_TITLE, 'mb-3')}>Top users</h2>
              <TopUsersTable data={detail.top_users} />
            </section>
          </>
        )}
      </div>
    </main>
  );
}
